import { useMutation } from '@apollo/client/react';
import {
  type CareerHistoryFormValues,
  normaliseCareerHistory,
} from '@seek/ca-myprofile-utils';
import { useMelwaysInfo } from '@seek/melways-react';
import { useFormContext } from 'react-hook-form';

import { gql } from 'src/__generated__';
import type { Role } from 'src/__generated__/graphql';
import type { ResumeFieldsValues } from 'src/components/ResumeFields/ResumeFields';

export const UPDATE_ROLE = gql(`
  mutation UpdateRole($input: UpdateRoleInput!) {
    updateRole(input: $input) {
      viewer {
        _id
        roles {
          ...role
        }
      }
    }
  }
`);

export const DELETE_ROLE = gql(`
  mutation DeleteRole($input: DeleteRoleInput!) {
    deleteRole(input: $input) {
      viewer {
        _id
        roles {
          ...role
        }
      }
    }
  }
`);

export const REJECT_ROLE = gql(`
  mutation RejectRole(
    $id: UUID!
    $contextId: String!
    $languageCode: LanguageCodeIso!
  ) {
    rejectUnconfirmedData(input: { id: $id, dataType: CareerHistory }) {
      viewer {
        _id
        roles {
          ...role
        }
        unconfirmedDataForContext(
          contextId: $contextId
          contextType: Application
          languageCode: $languageCode
        ) {
          ... on UnconfirmedDataCompleted {
            roles {
              ...unconfirmedRole
            }
          }
        }
      }
    }
  }
`);

export const CONFIRM_ROLE = gql(`
  mutation ConfirmRole(
    $input: ConfirmRoleInput!
    $contextId: String!
    $languageCode: LanguageCodeIso!
  ) {
    confirmRole(input: $input) {
      viewer {
        _id
        roles {
          ...role
        }
        unconfirmedDataForContext(
          contextId: $contextId
          contextType: Application
          languageCode: $languageCode
        ) {
          ... on UnconfirmedDataCompleted {
            roles {
              ...unconfirmedRole
            }
          }
        }
      }
    }
  }
`);

interface UseCareerHistoryMutationsProps {
  onAddRoleSuccess?: (role: Role) => void;
  onConfirmRoleSuccess?: (role: Role) => void;
  onEditRoleSuccess?: (role: Role) => void;
  onRejectRoleSuccess?: (role: Role) => void;
  onRemoveRoleSuccess?: (role: Role) => void;
}

export function useCareerHistoryMutations({
  onAddRoleSuccess,
  onConfirmRoleSuccess,
  onEditRoleSuccess,
  onRejectRoleSuccess,
  onRemoveRoleSuccess,
}: UseCareerHistoryMutationsProps = {}) {
  const { language } = useMelwaysInfo();
  const [update, stateUpdate] = useMutation(UPDATE_ROLE);
  const [remove, stateDelete] = useMutation(DELETE_ROLE);
  const [confirm] = useMutation(CONFIRM_ROLE);
  const [reject] = useMutation(REJECT_ROLE);

  // Get resumeId from the form
  const { watch } = useFormContext<ResumeFieldsValues>();
  const resumeId = watch('resume-select');

  const error = stateUpdate.error || stateDelete.error;

  const reset = () => {
    stateUpdate.reset();
    stateDelete.reset();
  };

  const updateRole = (id: string, role: Role) =>
    update({
      variables: { input: { ...role, id } },
    });

  const deleteRole = (id: string) => remove({ variables: { input: { id } } });

  const confirmRole = (role: Role) => {
    if (resumeId) {
      confirm({
        variables: {
          input: role,
          languageCode: language,
          contextId: resumeId,
        },
      });
    }
  };

  const rejectRole = (id: string) => {
    if (resumeId) {
      reject({
        variables: {
          id,
          languageCode: language,
          contextId: resumeId,
        },
      });
    }
  };

  const makeSubmitHandler =
    ({
      unconfirmedRoles,
      roles,
    }: {
      unconfirmedRoles: Role[];
      roles: Role[];
    }) =>
    async (values: CareerHistoryFormValues) => {
      const roleValues = normaliseCareerHistory(values);
      // confirmEdited find id in unconfirmed roles
      // then use confirmRole to update the role
      const found = unconfirmedRoles.find((r) => r.id === roleValues.id);
      if (found) {
        await confirmRole(roleValues);

        onConfirmRoleSuccess?.(found);
      } else {
        await updateRole(roleValues.id, roleValues);
        const isNewRole = !roles.find((r) => r.id === roleValues.id);

        if (isNewRole) {
          onAddRoleSuccess?.(roleValues);
        } else {
          onEditRoleSuccess?.(roleValues);
        }
      }
    };

  const makeDeleteHandler =
    ({
      unconfirmedRoles,
      roles,
    }: {
      unconfirmedRoles: Role[];
      roles: Role[];
    }) =>
    async (id: string) => {
      const found = unconfirmedRoles.find((r) => r.id === id);
      if (found) {
        await rejectRole(id);
        onRejectRoleSuccess?.(found);
      } else {
        const role = roles.find((r) => r.id === id);
        if (role) {
          await deleteRole(id);
          onRemoveRoleSuccess?.(role);
        }
      }
    };

  return {
    makeDeleteHandler,
    makeSubmitHandler,
    confirmRole,
    rejectRole,
    updateRole,
    deleteRole,
    error,
    reset,
  };
}
